import { GoogleGenAI } from "@google/genai";

const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY = 1000; // 1 second

export const getGeminiKey = () => {
  const key = (window as any).process?.env?.GEMINI_API_KEY || (window as any).process?.env?.API_KEY || process.env.GEMINI_API_KEY;
  if (!key) {
    console.warn("Gemini API key is missing from environment variables.");
  }
  return key;
};

export async function callGeminiWithRetry(fn: () => Promise<any>, retries = MAX_RETRIES, delay = INITIAL_RETRY_DELAY): Promise<any> {
  try {
    return await fn();
  } catch (error: any) {
    const isTransientError = 
      error?.message?.includes('503') || 
      error?.message?.includes('Service Unavailable') ||
      error?.message?.includes('504') ||
      error?.message?.includes('Gateway Timeout') ||
      error?.message?.includes('429') ||
      error?.message?.includes('Too Many Requests') ||
      error?.message?.includes('fetch failed');

    if (retries > 0 && isTransientError) {
      console.warn(`Gemini API error (retrying in ${delay}ms...):`, error.message);
      await new Promise(resolve => setTimeout(resolve, delay));
      return callGeminiWithRetry(fn, retries - 1, delay * 2);
    }
    throw error;
  }
}

export const chatWithGemini = async (
  messages: { 
    role: string, 
    content: string, 
    fileData?: string, 
    fileType?: string, 
    files?: { url: string, name: string, type: string, data?: string }[] 
  }[], 
  systemInstruction?: string
) => {
  const apiKey = getGeminiKey();
  if (!apiKey) throw new Error("Gemini API key is missing. Please configure it in settings.");
  
  const ai = new GoogleGenAI({ apiKey });
  
  const contents = await Promise.all(messages.map(async m => {
    const parts: any[] = [{ text: m.content }];
    
    if (m.files && m.files.length > 0) {
      for (const file of m.files) {
        if (file.data) {
          parts.push({
            inlineData: {
              data: file.data.split(',')[1] || file.data,
              mimeType: file.type
            }
          });
        } else if (file.type.startsWith('image/') || file.type === 'application/pdf' || file.type.startsWith('text/')) {
          try {
            const res = await fetch(file.url);
            if (!res.ok) throw new Error(`Failed to fetch ${file.url}`);
            const blob = await res.blob();
            const base64 = await new Promise<string>((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
              reader.readAsDataURL(blob);
            });
            parts.push({
              inlineData: {
                data: base64,
                mimeType: file.type
              }
            });
          } catch (e) {
            console.error("Failed to fetch file for Gemini:", e);
            parts.push({ text: `\n[Reference File: ${file.name} - ${file.url}]` });
          }
        } else {
          parts.push({ text: `\n[Reference File: ${file.name} - ${file.url}]` });
        }
      }
    }

    if (m.fileData && m.fileType && (!m.files || m.files.length === 0)) {
      if (m.fileData.startsWith('http')) {
        if (m.fileType.startsWith('image/')) {
          try {
            const res = await fetch(m.fileData);
            const blob = await res.blob();
            const base64 = await new Promise<string>((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
              reader.readAsDataURL(blob);
            });
            parts.push({
              inlineData: {
                data: base64,
                mimeType: m.fileType
              }
            });
          } catch (e) {
            console.error("Failed to fetch file for Gemini:", e);
            parts.push({ text: `[Attached File: ${m.fileData}]` });
          }
        } else {
          parts.push({ text: `\n[Reference File: ${m.fileData}]` });
        }
      } else {
        parts.push({
          inlineData: {
            data: m.fileData.split(',')[1] || m.fileData,
            mimeType: m.fileType
          }
        });
      }
    }
    return { 
      role: m.role === "user" ? "user" : "model", 
      parts 
    };
  }));

  const response = await Promise.race([
    callGeminiWithRetry(() => ai.models.generateContent({
      model: "gemini-3-flash-preview",
      contents,
      config: {
        systemInstruction: systemInstruction || `You are the Yacht Labs Assistant. Provide clear, accurate, and concise information. If the user asks for a project build, refer them to the Synthesis Engine.`,
        temperature: 1,
        topP: 0.95,
        topK: 64,
        maxOutputTokens: 8192,
        responseMimeType: "text/plain",
      }
    })),
    new Promise<any>((_, reject) => setTimeout(() => reject(new Error('Chat synthesis timed out')), 300000))
  ]);
  
  return response.text;
};

export const generateDesignPrompt = async (
  target: 'app' | 'landing' | 'custom' | 'deck' | 'brand' | 'bait' | 'landing_gen' | 'scaffolder',
  context: string,
  files?: { data: string, type: string }[],
  designSystem?: any
) => {
  const apiKey = getGeminiKey();
  if (!apiKey) throw new Error("Gemini API key is missing.");
  const ai = new GoogleGenAI({ apiKey });
  
  const targetLabel = 
    target === 'app' ? 'full-stack application' : 
    target === 'landing' ? 'landing page' : 
    'Project Brief';

  const designContext = designSystem ? `
    DESIGN IDENTITY SYSTEM (Inherit these tokens strictly):
    - Theme Category: ${designSystem.theme}
    - Primary Color: ${designSystem.tokens?.primary}
    - Background Color: ${designSystem.tokens?.background}
    - Text Color: ${designSystem.tokens?.text}
    - Typography: Headings use ${designSystem.typography?.heading}, Body uses ${designSystem.typography?.body}
    - Density: ${designSystem.density} layout
    - Style Primitives: Corner Radius: ${designSystem.componentStyle?.radius}, Shadows: ${designSystem.componentStyle?.shadow}, Component Style: ${designSystem.componentStyle?.style}
    
    ACCESSIBILITY & CONTRAST RULES:
    1. NEVER hardcode "text-white" or "bg-white" for primary content. Use CSS variables: var(--text), var(--background), var(--surface).
    2. Ensure text contrast ratio of at least 4.5:1. If the theme is Brutalist (White background), all text must be Black.
    3. Use var(--primary) for accents and CTA buttons.
    4. Maintain hierarchy using font weight and size, not just color shifts.
    
    Ensure all Tailwind classes and custom CSS reflect this precise identity.
  ` : '';

  const specificInstructions = 
    target === 'app' ? `
      Focus on:
      1. Functional Logic & State Management
      2. Comprehensive Data Flow
      3. User Authentication flows
      4. Database Schema representation
      5. Complex interactive components
      ${designContext}
    ` : 
    target === 'landing' ? `
      Focus on:
      1. Hero Section (Headline, Subheadline, CTA)
      2. Problem/Solution Framework
      3. Feature Breakdown
      4. Conversion-focused Copy
      ${designContext}
    ` : `
      Focus on overall project vision and architecture.
      ${designContext}
    `;

  const parts: any[] = [
    { text: `You are a world-class creative director and design engineer. 
    Your task is to synthesize the provided research and notes into a FULLY FUNCTIONAL, production-ready ${targetLabel}.
    
    The output MUST be a complete, self-contained project architecture consisting of functional HTML, CSS, and JS.
    
    ${specificInstructions}
    
    Context/Notes: ${context}` }
  ];

  if (files && files.length > 0) {
    for (const file of files.slice(0, 3)) {
      const base64Data = file.data.split(',')[1] || file.data;
      if (file.type.startsWith('image/') || file.type === 'application/pdf' || file.type.startsWith('text/')) {
        parts.push({
          inlineData: {
            data: base64Data,
            mimeType: file.type
          }
        });
      }
    }
  }

  const response = await Promise.race([
    callGeminiWithRetry(() => ai.models.generateContent({
      model: "gemini-3-flash-preview",
      contents: [{ parts }],
    config: {
        systemInstruction: `SYSTEM: You are the Yacht Labs Synthesis Engine, a senior design engineer and project architect. Your goal is to produce a ROBUST, HIGH-END Project Architecture using the latest modern design standards.

1. INTERNAL LOGIC: Before outputting, perform a thorough "Step-by-Step" analysis of the user's request. Wrap your internal reasoning in <thought> tags.
   - Analyze requirements, define data structures, map components, and plan the visual hierarchy.

2. STRUCTURE: ONLY output essential functional files. Every file must be functional and ready for immediate deployment.
   - Primary entry: index.html
   - Logic: script.js or main.js
   - Specialized styles: patterns.css (optional, preference for Tailwind utility classes)

3. MULTI-FILE LOGIC: Use the delimiter [FILE: filename.ext] before each file's content. This is CRITICAL for parsing.

4. DESIGN AESTHETIC:
   - FONT: Use "Inter" (sans-serif) as the primary font and "JetBrains Mono" for technical data or accents.
   - THEME: Default to a refined "SaaS Modern" look. Use high-contrast ratios (min 4.5:1).
   - STYLING: Use Tailwind CSS via CDN in index.html. Avoid custom CSS files unless necessary for complex animations.
   - ANIMATION: Incorporate subtle Framer Motion-style entrance animations using CSS transitions or simple script-based interactions.

5. BRAND IDENTITY (YACHT LABS):
   - FAVICON: Use this SVG in <head>: <link rel="icon" type="image/svg+xml" href="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 3 3'%3E%3Crect width='1' height='1' fill='white'/%3E%3Crect x='2' width='1' height='1' fill='white'/%3E%3Crect x='1' y='1' width='1' height='1' fill='white'/%3E%3Crect y='2' width='1' height='1' fill='white'/%3E%3Crect x='2' y='2' width='1' height='1' fill='white'/%3E%3C/svg%3E" />
   - LOGO: Use a 3x3 gapless grid: <div class="grid grid-cols-3 gap-0 w-6 h-6"><div class="bg-white"></div><div></div><div class="bg-white"></div><div></div><div class="bg-white"></div><div></div><div class="bg-white"></div><div></div><div class="bg-white"></div></div>

6. COMPONENT QUALITY:
   - ACCESSIBILITY: Use semantic HTML (<nav>, <main>, <section>, <article>, <footer>). Include aria-labels for buttons and inputs.
   - RESPONSIVENESS: Ensure mobile-first precision. Use Tailwind's sm:, md:, lg: prefixes for layouts.
   - CONTENT: Populate components with realistic, compelling data—no "Lorem Ipsum". Use vivid, industry-specific copy.

7. LINK INTEGRITY & NAVIGATION:
   - Use anchor links (#features, #pricing) and ensure the corresponding IDs exist.
   - For links that aren't implemented yet, use "javascript:void(0)" and trigger a "Coming Soon" toast message.

8. FORMATTING: Output strictly in Markdown. Wrap each file's content in exactly ONE set of triple backticks after its [FILE: ...] header.

9. NO ARTIFACTS: Do NOT include horizontal rules (---) or horizontal line placeholders between files. Do NOT truncate code.

10. COMMENCEMENT: Start immediately with [PROJECT ARCHITECTURE INITIALIZED].`,
        temperature: 0.8,
        topP: 0.95,
        topK: 64,
        maxOutputTokens: 8192,
        responseMimeType: "text/plain",
      }
    })),
    new Promise<any>((_, reject) => setTimeout(() => reject(new Error('Prompt generation timed out. Please try with fewer files or a shorter context.')), 300000))
  ]);

  return response.text;
};

export const editSynthesis = async (
  prompt: string, 
  currentState: string, 
  target: 'app' | 'file',
  designSystem?: any
) => {
  const apiKey = getGeminiKey();
  if (!apiKey) throw new Error("Gemini API key is missing.");
  const ai = new GoogleGenAI({ apiKey });

  const systemInstruction = `SYSTEM: You are the Yacht Labs Editor. Your goal is to MODIFY existing source code based on the user's request.
1. CONTEXT: You will receive the current source code (HTML/CSS/JS) of the application.
2. OUTPUT: Return the COMPLETE modified source code. Do not truncate.
3. REASONING: Include a "reasoning" array of strings in your JSON response detailing your design decisions.
4. FORMATTING: Return strictly valid JSON with two fields: "reasoning" (array) and "content" (string containing the full modified source code).
5. DESIGN ADHERENCE: Maintain the existing style and design system if provided.
6. PRECISION: Only modify the sections requested by the user, but return the full file.
7. BRAND INTEGRITY: Ensure the logo and trust links follow the brand standards (Grid-3, Gap-0 logo; no 404 links).`;

  const response = await ai.models.generateContent({
    model: "gemini-3.1-pro-preview",
    contents: [
      { role: 'user', parts: [{ text: `User Request: ${prompt}\n\nCurrent Source Code:\n${currentState}` }] }
    ],
    config: {
      systemInstruction,
      temperature: 0.7,
      responseMimeType: "application/json"
    }
  });

  const result = JSON.parse(response.text);
  return result;
};

export const validateSynthesis = async (originalGoal: string, generatedBuild: string) => {
  const apiKey = getGeminiKey();
  if (!apiKey) throw new Error("Gemini API key is missing.");
  const ai = new GoogleGenAI({ apiKey });
  
  const response = await Promise.race([
    callGeminiWithRetry(() => ai.models.generateContent({
      model: "gemini-3-flash-preview",
      contents: [{ 
        parts: [{ 
          text: `VALIDATION AUDIT: Evaluate the generated project build against the following Yacht Labs standards:
          
          ORIGNAL GOAL: ${originalGoal}
          
          STANDARDS:
          1. FIDELITY: Does it achieve the core functional goal?
          2. BRAND: Is the Yacht Labs 3x3 gapless grid logo implemented?
          3. DESIGN: Does it use semantic modern HTML sections and Inter font?
          4. CONTENT: Is the copy industry-specific and realistic (no lorem ipsum)?
          
          GENERATED BUILD (Sample):
          ${generatedBuild.substring(0, 3000)}
          
          Respond with "YES" if it passes all criteria. If NO, provide a 1-sentence instruction on what specific element is missing or incorrect.` 
        }] 
      }],
      config: {
        temperature: 0.1,
        responseMimeType: "text/plain",
      }
    })),
    new Promise<any>((_, reject) => setTimeout(() => reject(new Error('Validation timed out')), 60000))
  ]);

  const result = response.text.trim().toUpperCase();
  return {
    isValid: result.startsWith('YES'),
    reason: result.startsWith('NO') ? result.replace('NO', '').trim() : null
  };
};
